import { Button, FlexBox, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { useCallback } from 'react';

import { ConversationFlowAuditLogEntry } from 'apis/auditLogs';
import { useAppNavigate } from 'utils/url/useAppNavigate';

type RoutedThroughChatbotProps = {
  readonly node: ConversationFlowAuditLogEntry<'FLOW_CHATBOT_NODE_VISITED'>;
};

const exitReasonLabel: Record<string, string> = {
  HANDOFF_VIA_QUEUE: 'Handoff via queue',
  HANDOFF_VIA_BREAKING_CONTAINMENT: 'Handoff via continue with flow',
  HANDOFF_FAILED: 'Handoff',
  PROMPT_INJECTION: 'Prompt injection',
  BLOCKED_BY_CONTENT_POLICY: 'Blocked by content policy',
  UNABLE_TO_HANDLE: 'Unable to handle',
  UNSUPPORTED_MESSAGE: 'Unsupported message',
  UNKNOWN: 'Unknown',
};

export function RoutedThroughChatbot({
  node,
}: RoutedThroughChatbotProps): JSX.Element {
  const navigate = useAppNavigate();
  const hasAiAgentReadAccess = useHasAccess([
    'settings.integration.channel.edit',
    'settings.chatbot',
  ]);

  const handleGoToAiAgent = useCallback(() => {
    if (!node.chatbot_id) return;
    navigate({
      path: '/settings/ai-agents/mim/:id',
      params: {
        path: {
          id: node.chatbot_id,
        },
      },
    });
  }, [navigate, node.chatbot_id]);

  const paragraphStyle = node.skipped_due_to_quota
    ? { textDecoration: 'line-through' }
    : {};

  return (
    <FlexBox alignItems="center" gap="200">
      <Text.Paragraph>
        {node.skipped_due_to_quota && <span>(Skipped due to quota) </span>}

        <span style={paragraphStyle}>
          AI agent
          {node.chatbot_name && (
            <>
              : <strong>{node.chatbot_name}</strong>
            </>
          )}{' '}
          {node.exit_reason && exitReasonLabel[node.exit_reason] && (
            <>
              · <strong>{exitReasonLabel[node.exit_reason]}</strong>
            </>
          )}
        </span>
      </Text.Paragraph>
      {hasAiAgentReadAccess && node.chatbot_id && (
        <Button variant="secondary" onClick={handleGoToAiAgent}>
          Go to AI agent
        </Button>
      )}
    </FlexBox>
  );
}
